// Rep detail (WEB_PLAN.md §5.4, route /team/:profileId?day=YYYY-MM-DD): one person's day, the port of RepDetailView
// (GroundTeam/Views/Team/RepDetailView.swift). Header with the day's visits, contacts and appointments and the week's
// visits; a day picker that never goes past today; distance (day log, company unit), time in the field, visits this
// week and stores owned; the GPS trail with numbered visit pins in their result colors (or, switched to Route, the
// day's route stops numbered in visiting order); the timeline of that day's completed activities with their photos;
// and the day's route (RepRouteCard). Read-only in Phase 1 (Assign stores and Edit arrive with Phase 2 editing).
// Everything comes from the viewer's replica, so a rep opening their own day sees only what sync gave them.

import {
  Calendar,
  Car,
  ChevronLeft,
  ChevronRight,
  Clock,
  LocateOff,
  Map as MapIcon,
  MapPin,
  MessageSquare,
  Phone,
  Send,
  Mail,
  Sunrise,
  Sunset,
  Footprints,
  UserRoundX,
  BadgeCheck,
} from 'lucide-react';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { USER_ROLE_TITLES } from '@/contracts/enums';
import type { ActivityPayload, AttachmentPayload, UserPayload } from '@/contracts/models';
import { recordDisplayName } from '@/contracts/models';
import {
  useActivities,
  useAttachments,
  useDayLogs,
  useMetricsIndex,
  useRecords,
  useRepPositions,
  useRoutes,
  useSettings,
  useTeams,
  useUsers,
  useViewer,
  useVisibleUsers,
  useVisitResults,
  type DataItem,
} from '@/data/hooks';
import { isSameDay, resolveWeekStart, startOfDay } from '@/domain/calendar';
import { effectiveDate, isVerified } from '@/domain/derived';
import { formatDayDescription, formatDistance, formatElapsed, formatShortDate, formatShortTime } from '@/domain/format';
import { dayLogFor, repSummary } from '@/domain/metrics';
import { EMPTY_FILTER, activityDisplayTitle, filterToQuery } from '@/domain/records';
import { fullName, presenceOf } from '@/domain/team';
import { byId, idKey, sameId } from '@/domain/util';
import { RequireFirstSync, ScreenLoading, useDocumentTitle } from '@/features/common';
import { ActivityFileStrip } from '@/features/dashboard/ActivityFeedCard';
import { activityFiles, photoDetails, type ActivityFiles } from '@/features/dashboard/dashboardModel';
import { PhotoViewer } from '@/features/stores/FileTiles';
import {
  Avatar,
  Button,
  Card,
  DropdownMenu,
  EmptyState,
  GT_COLORS,
  IconBadge,
  IconButton,
  KpiTile,
  PRESENCE_COLORS,
  PRESENCE_TITLES,
  PageContainer,
  PageHeader,
  SectionCard,
  SegmentedControl,
  cn,
  controlClass,
  useTicker,
  type MenuEntry,
} from '@/ui';
import { ACTIVITY_GLYPHS } from './parts';
import {
  activityCountText,
  contactLinks,
  dayActivities,
  dayMilestones,
  dayParam,
  fieldMinutes,
  formatFieldTime,
  isLiveTrail,
  parseDayParam,
  shiftDay,
  trailCoordinates,
  visitPins,
  type Milestone,
  type VisitPin,
} from './repDay';
import { RepRouteCard } from './RepRouteCard';
import { RepTrailMap } from './RepTrailMap';
import { STOP_COLORS, routeCardModel, routeStopPins, routesForDay } from './repRouteModel';

/** The map's pins: the day's visits, or one of the day's routes (its id). */
const VISITS_VIEW = 'visits';
/** Route views offered next to Visits (a rep rarely has more than one route a day). */
const MAX_ROUTE_VIEWS = 3;

export default function RepDetailPage() {
  const { profileId } = useParams();
  return (
    <PageContainer width="default">
      <RequireFirstSync
        what="Rep detail"
        fallback={
          <>
            <PageHeader title="Rep" back={{ to: '/team', label: 'Team' }} />
            <ScreenLoading layout="cards" count={4} label="Loading the rep's day…" />
          </>
        }
      >
        <RepBody profileId={profileId ?? null} />
      </RequireFirstSync>
    </PageContainer>
  );
}

function RepBody({ profileId }: { profileId: string | null }) {
  const visible = useVisibleUsers();
  const item = visible.get(profileId);
  useDocumentTitle(item ? fullName(item.value) || 'Rep' : 'Rep');
  if (!item) {
    return (
      <>
        <PageHeader title="Rep" back={{ to: '/team', label: 'Team' }} />
        <EmptyState
          icon={<UserRoundX />}
          title="User not found"
          message="This user may have been removed, or isn't on a team you manage."
          action={
            <Link to="/team" className="text-sm font-semibold text-link hover:underline">
              Back to Team
            </Link>
          }
        />
      </>
    );
  }
  return <RepDay user={item.value} />;
}

function RepDay({ user }: { user: UserPayload }) {
  const navigate = useNavigate();
  const viewer = useViewer();
  const [searchParams, setSearchParams] = useSearchParams();
  const tick = useTicker(60_000);
  const now = useMemo(() => new Date(), [tick]);
  const todayKey = dayParam(now);
  const dayValue = searchParams.get('day');
  // Keyed on the date strings, so the day object (and everything computed from it) changes only when the day does.
  const day = useMemo(() => parseDayParam(dayValue, new Date()), [dayValue, todayKey]);
  const isToday = isSameDay(day, now);

  const setDay = useCallback(
    (next: Date) => {
      const params = new URLSearchParams(searchParams);
      if (isSameDay(next, new Date())) params.delete('day');
      else params.set('day', dayParam(next));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const activities = useActivities();
  const records = useRecords();
  const visitResults = useVisitResults();
  const dayLogs = useDayLogs();
  const index = useMetricsIndex();
  const teams = useTeams();
  const users = useUsers();
  const settings = useSettings();
  const positions = useRepPositions();
  const attachments = useAttachments();
  const routes = useRoutes();

  const weekStartsOn = useMemo(() => resolveWeekStart(), []);
  const log = useMemo(() => dayLogFor(dayLogs.values, user.id, day), [dayLogs.values, user.id, day]);
  const summary = useMemo(
    () => repSummary(index, user.id, { dayLogs: dayLogs.values, records: records.values }, day, { weekStartsOn }),
    [index, user.id, dayLogs.values, records.values, day, weekStartsOn],
  );
  const acts = useMemo(() => dayActivities(activities.values, user.id, day), [activities.values, user.id, day]);
  const visits = useMemo(() => acts.filter((a) => a.type === 'visit'), [acts]);
  const resultsById = useMemo(() => byId(visitResults.values), [visitResults.values]);
  const recordOf = useCallback((id: string | undefined) => records.get(id)?.value, [records]);
  const pins = useMemo(() => visitPins(visits, recordOf, resultsById), [visits, recordOf, resultsById]);
  const visitNumbers = useMemo(() => new Map(visits.map((v, i) => [idKey(v.id), i + 1])), [visits]);
  const pinColors = useMemo(() => new Map(pins.map((p) => [idKey(p.id), p.color])), [pins]);
  const trail = useMemo(() => trailCoordinates(log), [log]);
  const milestones = dayMilestones(log, isToday);
  const live = isLiveTrail(log, isToday);
  const usersById = useMemo(() => byId(users.values), [users]);

  // The day's routes ("now" moves an active route's driven distance and time on the road).
  const routeModels = useMemo(
    () => routesForDay(routes.values, user.id, day, now).map((r) => routeCardModel(r, { recordOf, dayLogs: dayLogs.values, now })),
    [routes, user.id, day, now, recordOf, dayLogs],
  );
  const routeViews = useMemo(
    () => routeModels.filter((r) => r.stops.some((s) => s.coordinate)).slice(0, MAX_ROUTE_VIEWS),
    [routeModels],
  );
  const [mapViewChoice, setMapView] = useState<string>(VISITS_VIEW);
  // Another day may not have the chosen route: back to the visits.
  const shownRoute = routeViews.find((r) => r.id === mapViewChoice);
  const mapView = shownRoute ? shownRoute.id : VISITS_VIEW;
  const mapPins = useMemo(() => (shownRoute ? routeStopPins(shownRoute.stops) : pins), [shownRoute, pins]);
  // Numbered like the route card ("Route 2"): routes built from the agenda all share a day's name ("Agenda · Oct 8").
  const mapViewOptions = useMemo(
    () => [
      { value: VISITS_VIEW, label: 'Visits' },
      ...routeViews.map((r) => {
        if (routeModels.length === 1) return { value: r.id, label: 'Route' };
        const n = routeModels.indexOf(r) + 1;
        return {
          value: r.id,
          label: (
            <span title={r.name}>
              Route {n}
              <span className="sr-only">: {r.name}</span>
            </span>
          ),
        };
      }),
    ],
    [routeViews, routeModels],
  );

  const [photo, setPhoto] = useState<{ item: DataItem<AttachmentPayload>; loggedWith: string } | null>(null);

  const name = fullName(user) || 'Unnamed';
  const team = teams.get(user.teamId)?.value;
  const manager = users.get(user.managerId)?.value;
  const position = positions.get(idKey(user.id));
  const presence = presenceOf(position?.at, now);
  const dayLabel = isToday ? 'Today' : formatShortDate(day);

  const go = (delta: number) => {
    const next = shiftDay(day, delta, new Date());
    if (next) setDay(next);
  };

  const openPin = (pin: VisitPin) => {
    if (pin.recordId) navigate(`/stores/${pin.recordId}`);
  };

  return (
    <>
      <PageHeader title={name} back={{ to: '/team', label: 'Team' }} actions={<MessageMenu user={user} />} />

      <div className="flex flex-col gap-5">
        {/* Hero: who, and the day's numbers (RepHeroIdentity + HeroStat). */}
        <section
          aria-label={`${name}, ${dayLabel}`}
          className="rounded-card bg-[linear-gradient(135deg,var(--gt-navy)_0%,#1F4A9A_100%)] p-5 text-white shadow-card"
        >
          <div className="flex items-start gap-4">
            <Avatar name={name} colorHex={user.colorHex} size={62} presence={presence} label={null} />
            <div className="min-w-0 flex-1">
              <p className="text-xl leading-tight font-bold sm:text-2xl">{name}</p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <HeroChip>{USER_ROLE_TITLES[user.role]}</HeroChip>
                {team && <HeroChip>{team.name}</HeroChip>}
                {!user.isActive && <HeroChip>Inactive</HeroChip>}
              </div>
              {user.title && <p className="mt-1.5 text-xs text-white/85">{user.title}</p>}
              <p className="mt-1 flex items-center gap-1.5 text-xs text-white/85">
                <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: PRESENCE_COLORS[presence] }} />
                {PRESENCE_TITLES[presence]}
                {position && <span>· {formatElapsed(position.at, now)}</span>}
              </p>
              {manager && <p className="mt-0.5 text-[11px] text-white/70">Reports to {fullName(manager)}</p>}
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-end gap-x-8 gap-y-3">
            <HeroStat value={summary.visits} label="Visits" />
            <HeroStat value={summary.contacts} label="Contacts" />
            <HeroStat value={summary.appointments} label="Appts" />
            <div className="ml-auto text-right">
              <p className="text-[11px] font-bold tracking-[0.06em] uppercase opacity-80">{dayLabel}</p>
              <p className="text-[11px] tabular-nums opacity-75">
                {summary.weekVisits} {summary.weekVisits === 1 ? 'visit' : 'visits'} this week
              </p>
            </div>
          </div>
        </section>

        {/* Day picker (no future days). */}
        <Card padding="sm" className="flex flex-wrap items-center gap-2">
          <IconButton label="Previous day" icon={<ChevronLeft />} onClick={() => go(-1)} />
          <input
            type="date"
            aria-label="Day"
            value={dayParam(day)}
            max={todayKey}
            onChange={(e) => {
              if (e.target.value) setDay(parseDayParam(e.target.value, new Date()));
            }}
            className={cn(controlClass, 'h-9 w-auto px-3 text-sm')}
          />
          <span className="text-sm font-semibold text-fg">{formatDayDescription(day, now)}</span>
          <span className="flex-1" />
          {!isToday && (
            <Button size="sm" onClick={() => setDay(startOfDay(new Date()))}>
              Today
            </Button>
          )}
          <IconButton label="Next day" icon={<ChevronRight />} disabled={isToday} onClick={() => go(1)} />
        </Card>

        <section aria-label={isToday ? 'Today in the field' : dayLabel} className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiTile label="Distance" value={formatDistance(settings.distanceUnit, summary.meters)} icon={<Car />} color={GT_COLORS.blue} />
          <KpiTile label="In field" value={formatFieldTime(fieldMinutes(log, isToday, now))} icon={<Clock />} color={GT_COLORS.green} />
          <KpiTile label="This week" value={summary.weekVisits.toLocaleString()} sub="visits" icon={<Calendar />} color={GT_COLORS.orange} />
          <KpiTile
            label="Stores"
            value={summary.ownedRecords.toLocaleString()}
            sub="owned"
            icon={<MapPin />}
            color={GT_COLORS.navy}
            to={`/stores?${filterToQuery({ ...EMPTY_FILTER, ownerIds: [user.id] }).toString()}`}
          />
        </section>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <SectionCard
            title="Route & visits"
            action={
              trail.length > 0 ? (
                <span className="text-xs text-fg-muted tabular-nums">{trail.length.toLocaleString()} GPS points</span>
              ) : undefined
            }
            padding="none"
            bodyClassName="overflow-hidden"
          >
            {/* In the card, not the title row: the title row does not wrap, and a phone has no room for both. */}
            {routeViews.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto border-b border-line px-3 py-2">
                <SegmentedControl
                  label="Pins on the map"
                  value={mapView}
                  onChange={setMapView}
                  options={mapViewOptions}
                  size="sm"
                  uppercase={false}
                />
              </div>
            )}
            {trail.length === 0 && mapPins.length === 0 ? (
              <EmptyState
                compact
                icon={<MapIcon />}
                title={isToday ? 'Nothing on the map yet' : 'No trail recorded'}
                message={
                  isToday
                    ? 'The trail draws as the rep moves with tracking on. Visits appear as pins.'
                    : 'No GPS breadcrumbs or located visits were captured on this day.'
                }
              />
            ) : (
              <div className="relative h-[360px]">
                <RepTrailMap
                  trail={trail}
                  pins={mapPins}
                  live={live}
                  // The stops have their own legend (RouteStopsLegend); the visits are not drawn then.
                  visitCount={shownRoute ? null : visits.length}
                  // Switching between visits and a route fits the camera to the new pins.
                  fitKey={`${dayParam(day)}|${mapView}`}
                  onPinClick={openPin}
                  ariaLabel={
                    shownRoute
                      ? `Map of ${name}'s trail and the ${shownRoute.stops.length} stops of ${shownRoute.name} on ${dayLabel}`
                      : `Map of ${name}'s route and ${visits.length} ${visits.length === 1 ? 'visit' : 'visits'} on ${dayLabel}`
                  }
                />
                {shownRoute && <RouteStopsLegend finished={shownRoute.status === 'completed'} />}
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Timeline"
            action={<span className="text-xs text-fg-muted tabular-nums">{activityCountText(acts.length)}</span>}
            padding="sm"
          >
            <ol className="flex flex-col">
              <MilestoneRow milestone={milestones.start} />
              {acts.length === 0 ? (
                <li className="py-3 pl-[76px] text-xs text-fg-subtle">
                  {isToday ? 'No activities logged yet today.' : 'No activities were logged on this day.'}
                </li>
              ) : (
                acts.map((a) => {
                  const title = activityDisplayTitle(a, resultsById);
                  return (
                    <TimelineRow
                      key={a.id}
                      activity={a}
                      title={title}
                      storeName={a.recordId ? recordName(recordOf(a.recordId)) : null}
                      visitNumber={visitNumbers.get(idKey(a.id))}
                      pinColor={pinColors.get(idKey(a.id))}
                      files={activityFiles(a.attachmentIds, (id) => attachments.get(id))}
                      onOpenPhoto={(item) => setPhoto({ item, loggedWith: title })}
                    />
                  );
                })
              )}
              {milestones.end && <MilestoneRow milestone={milestones.end} />}
            </ol>
          </SectionCard>
        </div>

        <RepRouteCard routes={routeModels} isToday={isToday} distanceUnit={settings.distanceUnit} />

        {/* iOS leaves the note out on one's own profile. */}
        {!user.trackingEnabled && !sameId(user.id, viewer?.profileId) && (
          <p className="flex items-center gap-1.5 text-xs text-fg-muted">
            <LocateOff aria-hidden className="size-3.5" />
            Location tracking is off for this rep
          </p>
        )}
      </div>
      {photo && (
        <PhotoViewer
          item={photo.item}
          onClose={() => setPhoto(null)}
          details={photoDetails(photo.item.value, { users: usersById, loggedWith: photo.loggedWith, now })}
        />
      )}
    </>
  );
}

/**
 * What the numbered pins mean while a route is shown (STOP_COLORS; the map's own legend covers the trail): the
 * running route's next stop and the ones to do, or how a finished route's stops ended.
 */
function RouteStopsLegend({ finished }: { finished: boolean }) {
  const entries = finished
    ? [
        { label: 'Done', color: STOP_COLORS.done },
        { label: 'Arrived', color: STOP_COLORS.arrived },
        { label: 'Not visited', color: STOP_COLORS.missed },
        { label: 'Skipped', color: STOP_COLORS.skipped },
      ]
    : [
        { label: 'Done', color: STOP_COLORS.done },
        { label: 'Next', color: STOP_COLORS.next },
        { label: 'To do', color: STOP_COLORS.pending },
        { label: 'Skipped', color: STOP_COLORS.skipped },
      ];
  return (
    <div className="pointer-events-none absolute top-2 left-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full bg-surface/90 px-3 py-1.5 text-[11px] text-fg shadow-card backdrop-blur">
      <span className="font-semibold">Route stops</span>
      {entries.map((e) => (
        <span key={e.label} className="inline-flex items-center gap-1.5">
          <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: e.color }} />
          {e.label}
        </span>
      ))}
    </div>
  );
}

function recordName(record: { name: string; company: string } | undefined): string | null {
  if (!record) return null;
  return recordDisplayName(record) || 'Untitled';
}

function HeroChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-6 items-center rounded-full border border-white/25 bg-white/15 px-2.5 text-xs font-semibold">
      {children}
    </span>
  );
}

function HeroStat({ value, label }: { value: number; label: string }) {
  return (
    <div>
      <p className="font-rounded text-[34px] leading-none font-bold tabular-nums">{value.toLocaleString()}</p>
      <p className="mt-1 text-[11px] font-semibold tracking-[0.06em] uppercase opacity-80">{label}</p>
    </div>
  );
}

/** Timeline milestone glyphs (RepDetailView.milestone): sunrise / sunset when recorded, walking while out, gray when missing. */
function milestoneLook(m: Milestone): { icon: ReactNode; color: string } {
  if (m.tone === 'live') return { icon: <Footprints />, color: GT_COLORS.green };
  if (m.edge === 'start') return { icon: <Sunrise />, color: m.tone === 'recorded' ? GT_COLORS.amber : GT_COLORS.grey };
  return { icon: <Sunset />, color: m.tone === 'recorded' ? GT_COLORS.purple : GT_COLORS.grey };
}

function MilestoneRow({ milestone }: { milestone: Milestone }) {
  const look = milestoneLook(milestone);
  return (
    <li className="flex items-center gap-3 py-1.5">
      <span className="w-[52px] shrink-0 text-center text-[10px] font-semibold text-fg-muted tabular-nums">
        {milestone.time ? formatShortTime(milestone.time) : '—'}
      </span>
      <IconBadge icon={look.icon} color={look.color} size={28} />
      <span className="text-sm font-semibold text-fg">{milestone.title}</span>
    </li>
  );
}

function TimelineRow({
  activity,
  title,
  storeName,
  visitNumber,
  pinColor,
  files,
  onOpenPhoto,
}: {
  activity: ActivityPayload;
  title: string;
  storeName: string | null;
  visitNumber?: number | undefined;
  pinColor?: string | undefined;
  /** The activity's photos and documents (null when it has none). */
  files: ActivityFiles | null;
  onOpenPhoto: (item: DataItem<AttachmentPayload>) => void;
}) {
  const glyph = ACTIVITY_GLYPHS[activity.type];
  const verified = isVerified(activity);
  const body = (
    <>
      <span className="flex min-w-0 items-center gap-1.5">
        {visitNumber !== undefined && <span className="sr-only">Visit {visitNumber}:</span>}
        <span className="truncate text-sm font-semibold text-fg">{title}</span>
        {verified && <BadgeCheck aria-label="Verified" className="size-3.5 shrink-0 text-success-text" />}
      </span>
      {storeName && <span className="block truncate text-xs text-fg-muted">{storeName}</span>}
      {activity.notes.trim() !== '' && <span className="line-clamp-1 text-xs text-fg-subtle">{activity.notes}</span>}
    </>
  );
  return (
    <li className="flex items-stretch gap-3">
      <span className="flex w-[52px] shrink-0 flex-col items-center">
        <span aria-hidden className="h-2 w-0.5 bg-surface-3" />
        <span className="text-[10px] font-semibold text-fg-muted tabular-nums">{formatShortTime(effectiveDate(activity))}</span>
        <span aria-hidden className="w-0.5 flex-1 bg-surface-3" />
      </span>
      <span className="relative mt-1.5 shrink-0">
        <IconBadge icon={glyph.icon} color={glyph.color} size={28} />
        {visitNumber !== undefined && (
          <span
            aria-hidden
            className="absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full text-[9px] font-bold text-white ring-2 ring-surface"
            style={{ backgroundColor: pinColor ?? GT_COLORS.grey }}
          >
            {visitNumber}
          </span>
        )}
      </span>
      <div className="min-w-0 flex-1">
        {activity.recordId ? (
          <Link
            to={`/stores/${activity.recordId}`}
            className="my-1 block min-w-0 rounded-control px-1.5 py-1 hover:bg-surface-2 focus-visible:shadow-[inset_0_0_0_2px_var(--gt-focus)] focus-visible:outline-none"
          >
            {body}
          </Link>
        ) : (
          <span className="my-1 block min-w-0 px-1.5 py-1">{body}</span>
        )}
        {/* Outside the link: a thumbnail is a button of its own. */}
        {files && <ActivityFileStrip files={files} onOpenPhoto={onOpenPhoto} className="mb-2 px-1.5" />}
      </div>
    </li>
  );
}

/** "Send message" (SendMessageLabel menu): text, call or email the rep from this computer's apps. */
function MessageMenu({ user }: { user: UserPayload }) {
  const links = contactLinks(user);
  const items: MenuEntry[] = [];
  if (links.sms) items.push({ id: 'sms', label: `Text ${user.phone}`, icon: <MessageSquare />, onSelect: () => window.location.assign(links.sms!) });
  if (links.tel) items.push({ id: 'tel', label: `Call ${user.phone}`, icon: <Phone />, onSelect: () => window.location.assign(links.tel!) });
  if (links.mail) items.push({ id: 'mail', label: `Email ${user.email.trim()}`, icon: <Mail />, onSelect: () => window.location.assign(links.mail!) });
  if (items.length === 0) {
    return (
      <Button icon={<Send />} disabled title="Add a phone or email to message this rep">
        Send message
      </Button>
    );
  }
  return (
    <DropdownMenu
      label="Send message"
      placement="bottom-end"
      items={items}
      trigger={
        <Button variant="primary" icon={<Send />}>
          Send message
        </Button>
      }
    />
  );
}
